import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import { timer } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AppConfigurationService } from 'src/app/services/app.configuration.service';
import { AuthService } from 'src/app/services/auth.service';
import { ProfileService } from 'src/app/services/profile.service';
import { getPendingPscSession, MangopayIdvSession } from 'src/app/shared/models/register.models';
import { isMobileWeb, mangopayIframeCamera, requestMobileWebCamera } from 'src/app/shared/utils/camera-permission';

@Component({
  selector: 'app-mangopay-sca-validation',
  templateUrl: './mangopay-sca-validation.component.html',
  styleUrls: ['./mangopay-sca-validation.component.scss'],
})
export class MangopayScaValidationComponent implements OnInit, OnDestroy {

  @Input() public isOwner: boolean = true;
  @Input() public hasConsentGranted: boolean = true;
  // OWNER que todavía no ha completado el SCA en Mangopay
  @Input() public needsSca: boolean = false;
  @Input() public mangopayIdvSession: MangopayIdvSession;
  @Output() public finishEvent = new EventEmitter<any>();
  @Output() public removeModalEvent = new EventEmitter<any>();

  urlSafe: SafeResourceUrl;
  // El iframe de Mangopay lleva permiso de cámara salvo en web de escritorio (ahí Mangopay muestra el QR)
  cameraInIframe = mangopayIframeCamera();
  // En web móvil la barra inferior del navegador tapa los botones de Mangopay: se deja espacio para hacer scroll
  mobileWebSpacing = isMobileWeb();
  iframeLoaded: boolean = false;
  step: number = 0;

  text: string;
  image: number;
  btnList = [
    {
      type: 'primaryDark',
      index: 1,
      title: this.translate.instant('bid-create.continue'),
    }
  ];

  loading: boolean = false;
  private currentFlow: 'sca' | 'consent' | 'psc' | null = null;
  // El resultado del SCA se manda una sola vez: un false tardío pisaba en BD el SCA ya confirmado
  private scaResultSent = false;
  private messageHandler = (e: MessageEvent) => this.handleMessage(e);

  constructor(
    private sanitizer: DomSanitizer,
    private cdRef: ChangeDetectorRef,
    private translate: TranslateService,
    private profileService: ProfileService,
    private authService: AuthService,
    private appConfigurationService: AppConfigurationService,
    private elementRef: ElementRef
  ) { }

  ngOnInit() {
    const idvAlreadyValidated = this.mangopayIdvSession?.status === 'VALIDATED';
    if ((!this.isOwner && !idvAlreadyValidated) || this.needsSca) {
      this.currentFlow = 'sca';
      this.startScaFlow();
    } else if (!this.hasConsentGranted) {
      this.currentFlow = 'consent';
      this.startConsentFlow();
    } else if (getPendingPscSession(this.mangopayIdvSession)) {
      this.currentFlow = 'psc';
      this.startPscFlow();
    } else {
      this.finish();
      return;
    }

    window.addEventListener('message', this.messageHandler);
    this.cdRef.detectChanges();
  }

  ngOnDestroy() {
    window.removeEventListener('message', this.messageHandler);
  }

  // Solo cuentan los mensajes del iframe de este componente: las páginas de vuelta de SCA, consentimientos e IDV
  // mandan el mismo control_status, y un listener de otra pantalla los tomaba como resultado del SCA
  private isFromOwnIframe(e: MessageEvent): boolean {
    const iframes: HTMLIFrameElement[] = Array.from(this.elementRef.nativeElement.querySelectorAll('iframe'));
    return iframes.some(iframe => iframe.contentWindow === e.source);
  }

  private handleMessage(e: MessageEvent) {
    if ((e.data as any)?.func !== 'control_status' || !this.isFromOwnIframe(e)) { return; }
    const status = (e.data as any).data.value.controlStatus;
    this.step = 1;
    this.loading = true;

    if (this.currentFlow === 'sca') {
      if (this.scaResultSent) { return; }
      this.scaResultSent = true;
      const validated = status === 'VALIDATED';
      if (validated) {
        this.setValidatedMessageParams();
      } else {
        this.image = 1;
        this.text = this.translate.instant('SCA.ISSUE');
      }
      this.profileService.saveCategorizeResult(validated).subscribe(() => {
        this.loading = false;
        if (validated && !this.hasConsentGranted) {
          this.step = 0;
          this.iframeLoaded = false;
          this.urlSafe = null;
          this.currentFlow = 'consent';
          this.startConsentFlow();
        } else {
          this.currentFlow = null;
          this.finish();
        }
        this.cdRef.detectChanges();
      });
    } else if (this.currentFlow === 'consent') {
      if (status === 'VALIDATED') {
        this.setValidatedMessageParams();
      } else {
        this.image = 1;
        this.text = this.translate.instant('SCA.ISSUE');
      }
      this.loading = false;
      this.currentFlow = null;
      this.finish();
    } else if (this.currentFlow === 'psc') {
      // Aunque la PSC haya terminado, Mangopay aún tiene que revisarla antes de dar el KYC REGULAR
      this.image = 6;
      this.text = this.translate.instant('register.identity_verification.pending');
      this.loading = false;
    }
    this.cdRef.detectChanges();
  }

  private startScaFlow() {
    this.profileService.categorizeUser().pipe(
      catchError(() => timer(2000).pipe(switchMap(() => this.profileService.categorizeUser())))
    ).subscribe(resp => {
      if (resp === 'Categorized' || resp.returnUrl === null) {
        if (!this.hasConsentGranted || resp.returnUrl === null) {
          this.currentFlow = 'consent';
          this.startConsentFlow();
        } else {
          this.setValidatedMessageParams();
          this.step = 1;
        }
      } else {
        this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(resp.returnUrl);
      }
      this.cdRef.detectChanges();
    }, () => {
      // Un OWNER puede estar ya enrolado en Mangopay aunque en BD no conste: si falla el
      // enrolamiento no le bloqueamos y seguimos como antes (consentimientos o continuar)
      if (this.isOwner) {
        if (!this.hasConsentGranted) {
          this.currentFlow = 'consent';
          this.startConsentFlow();
        } else {
          this.finish();
        }
        this.cdRef.detectChanges();
        return;
      }
      this.step = 1;
      this.image = 1;
      this.text = this.translate.instant('SCA.ISSUE');
      this.loading = false;
      this.cdRef.detectChanges();
    });

  }

  private startConsentFlow() {
    this.profileService.getMangopayConsent().subscribe(resp => {
      this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(resp.returnUrl);
      this.cdRef.detectChanges();
    }, error => {
      console.log(error, 'error');
    });
  }

  // El enlace de la PSC pendiente ya viene en la sesión IDV del perfil
  private startPscFlow() {
    // La PSC hace su selfie en el iframe: en web móvil, la cámara se pide antes de abrirlo (ver requestMobileWebCamera)
    requestMobileWebCamera().then(() => {
      this.urlSafe = this.sanitizer.bypassSecurityTrustResourceUrl(getPendingPscSession(this.mangopayIdvSession).hostedUrl);
      this.cdRef.detectChanges();
    });
  }

  charged(event) {
    // Al insertar el iframe el navegador lanza un primer load de la página vacía (about:blank, aún sin src):
    // tomarlo como cargado quitaba el "Validando" antes de que llegara Mangopay y se veía la pantalla en blanco
    const src = (event?.target as HTMLIFrameElement)?.src;
    if (!src || src === 'about:blank') { return; }
    setTimeout(() => {
      this.iframeLoaded = true;
      this.cdRef.detectChanges();
    }, 1000);
    this.cdRef.detectChanges();
  }

  setValidatedMessageParams() {
    this.image = 6;
    this.text = this.translate.instant('SCA.CAN_CONTINUE');
    this.cdRef.detectChanges();
  }

  finish() {
    this.finishEvent.emit();
    this.cdRef.detectChanges();
  }

  removeModal() {
    this.removeModalEvent.emit();
    this.cdRef.detectChanges();
  }

}
